<template>
  <div class="page-container studio-page" ref="scroller">
    <Header dark />
    <!-- Text comes from Branding/Website copy: change it there first, then here. -->
    <section class="studio-banner">
      <p class="eyebrow">Game studio</p>
      <h1 class="studio-title">Fluorine Republika</h1>
      <p class="studio-line studio-tagline">We dissolve the bad parts of games. Not the fun.</p>
      <p class="studio-line">
        Fluorine Republika is a one-person game studio. We make games that are fun first and fair always:
        no forced ads, nothing that sells power, and you always know what you're paying for.
        Mobile games first, then PC, and one day VR.
      </p>
      <a class="constitution-link" href="#constitution" @click.prevent="scrollTo('constitution')">Read the Constitution ↓</a>
    </section>

    <ul class="promise-strip">
      <li>No forced ads, ever</li>
      <li>Never pay for power</li>
      <li>Prices up front, no gems</li>
    </ul>

    <nav class="section-menu" aria-label="Sections">
      <a v-for="item in menu" :key="item.id" :href="'#' + item.id" @click.prevent="scrollTo(item.id)">{{ item.label }}</a>
    </nav>

    <section id="games" class="block">
      <h2 class="block-title">Games</h2>
      <article v-for="game in games" :id="game.slug" :key="game.slug" class="studio-banner game-section">
        <div class="game-row">
          <div class="game-card-image game-image">
            <img v-if="game.image" :src="game.image" :alt="game.name" :class="game.imageFit">
            <span v-else class="game-card-initial">{{ (game.shortName || game.name).charAt(0) }}</span>
          </div>
          <div class="game-text">
            <span class="badge">{{ game.status }} · <PlatformList :platforms="game.platforms" /></span>
            <h3 class="game-title">{{ game.name }} <span v-if="game.codename" class="working-title" title="Working title. The real name comes at launch.">Codename</span></h3>
            <p>{{ game.tagline }}</p>
            <div class="how-you-pay">
              <span class="how-you-pay-label">How you pay</span>
              <p>{{ game.howYouPay }}</p>
            </div>
            <div class="buttons">
              <template v-for="link in game.links" :key="link.label">
                <a v-if="!link.soon" class="studio-button" :href="link.url" target="_blank" rel="noopener">{{ link.label }}</a>
                <span v-else class="studio-button soon" title="Coming soon">{{ link.label }} (soon)</span>
              </template>
              <router-link class="studio-button ghost" :to="{ name: 'Game', params: { slug: game.slug } }">Deep dive →</router-link>
            </div>
          </div>
        </div>
      </article>
    </section>

    <section id="constitution" class="block">
      <h2 class="block-title">The Republika</h2>
      <div class="studio-banner prose">
        <h3>Why the name</h3>
        <p>
          Fluorine is the most reactive element there is. It eats through almost anything.
          We aim it at the bad habits of games: forced ads, pay-to-win, timers that nag you to pay,
          and prices you only find out about too late. The fun stays.
        </p>
      </div>

      <div class="decree">
        <p class="decree-eyebrow">Official decree</p>
        <h3 class="decree-title">The Constitution of the Republika</h3>
        <p class="decree-intro">Every game we make keeps these promises.</p>
        <ol class="articles">
          <li v-for="(article, i) in articles" :key="i">
            <span class="article-number">{{ toRoman(i + 1) }}</span>
            <span class="article-text">{{ article }}</span>
          </li>
        </ol>
        <div class="seal" aria-hidden="true">
          <span class="seal-ring">Issued by the Fluorine Republika</span>
          <span class="seal-mark">F</span>
        </div>
        <p class="decree-signature">Issued by the Fluorine Republika.</p>
      </div>
    </section>

    <section id="business" class="block">
      <h2 class="block-title">How we make money</h2>
      <div class="studio-banner prose">
        <p>
          Honestly: a studio has to earn money to keep making games, and we're no different.
          What's different is how. We sell the game, new chapters and looks, never power or playability.
          You know what you're paying for before you pay, the price is in real money, and there's no small print.
        </p>
        <div class="table-wrap">
          <table class="money-table">
            <thead>
              <tr><th>Game</th><th>Price</th><th>What you pay for</th></tr>
            </thead>
            <tbody>
              <tr v-for="game in games" :key="game.slug">
                <td>{{ game.shortName || game.name }}</td>
                <td>{{ game.pricing.price }}</td>
                <td>{{ game.pricing.payFor }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section id="road" class="block">
      <h2 class="block-title">Where we're going</h2>
      <div class="studio-banner prose">
        <ol class="timeline">
          <li class="now">
            <span class="dot"></span>
            <strong>Mobile</strong>
            <span>Now: BOTHMCB, BoxDrop, Project Lantern</span>
          </li>
          <li>
            <span class="dot"></span>
            <strong>PC</strong>
            <span>Next: Desk Pond</span>
          </li>
          <li>
            <span class="dot"></span>
            <strong>VR</strong>
            <span>One day: Yesterday Folks</span>
          </li>
        </ol>
        <p>The goal is a republic of players where fair games are the norm, not the exception.</p>
        <h3>Who</h3>
        <p>
          A one-person studio by Florin Lica. I design, code and ship everything myself.
          <router-link :to="{ name: 'OtherWork' }">See my other work →</router-link>
        </p>
      </div>
    </section>
    <SiteFooter dark />
  </div>
</template>

<script>
import Header from './header.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import PlatformList from '@/components/PlatformList.vue';
import games from '@/data/games';

export default {
  name: 'FluorineRepublika',
  components: { Header, SiteFooter, PlatformList },
  data() {
    return {
      games,
      menu: [
        { id: 'games', label: 'Games' },
        { id: 'constitution', label: 'Constitution' },
        { id: 'business', label: 'Business' },
        { id: 'road', label: 'Road' }
      ],
      articles: [
        'You will never be forced to watch an ad.',
        'Everything that affects play can be earned by playing. We sell the game, new chapters and looks, never playability.',
        'Money buys the game, new chapters, or looks. Never power. Power-ups are earned by playing, and an optional ad is only a shortcut.',
        'You know what you pay for before you pay. Prices are in real money, with no small print, no gems, no loot boxes and no countdowns.',
        'What you buy is yours to keep.'
      ]
    };
  },
  mounted() {
    this.scrollToHash();
  },
  watch: {
    '$route.hash'() {
      this.scrollToHash();
    }
  },
  methods: {
    // Pages scroll inside .page-container, so the router can't scroll to #anchors by itself.
    scrollToHash() {
      const hash = this.$route.hash;
      if (!hash) return;
      this.$nextTick(() => this.scrollTo(hash.slice(1)));
    },
    scrollTo(id) {
      const target = document.getElementById(id);
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    },
    toRoman(n) {
      return ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'][n - 1];
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../assets/styles/global.scss' as *;
@use '../assets/styles/studio.scss' as *;

.constitution-link {
  color: var(--fluorine);
  font-size: 17px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.promise-strip {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 28px;
  margin: 0 0 10px;
  padding: 0 16px;
  list-style: none;
  color: var(--fluorine);
  font-family: "Times New Roman";
  font-size: 20px;
  li::before {
    content: '✓ ';
    opacity: 0.7;
  }
}

// Sticks just under the header bar while scrolling.
.section-menu {
  position: sticky;
  top: 92px;
  z-index: 40;
  display: flex;
  gap: 4px;
  margin: 14px 0 6px;
  padding: 6px;
  border-radius: 999px;
  background: rgba(36, 34, 26, 0.92);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  a {
    padding: 6px 14px;
    border-radius: 999px;
    color: var(--fluorine-text);
    font-family: "PT Sans Narrow", sans-serif;
    font-size: 16px;
    text-decoration: none;
    &:hover {
      background: #000;
      color: var(--fluorine);
    }
  }
}

.block {
  box-sizing: border-box;
  width: 90%;
  scroll-margin-top: 150px;
}
.block-title {
  margin: 36px 0 0;
  color: var(--fluorine);
  font-family: "Times New Roman";
  font-size: 32px;
  font-weight: 400;
  text-align: center;
}
.block > .studio-banner {
  width: 100%;
}

.game-section {
  text-align: left;
  scroll-margin-top: 150px;
}
.game-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}
.game-image {
  flex: 0 0 240px;
  height: 300px;
}
.game-text {
  flex: 1;
  font-family: "PT Sans Narrow";
  font-size: 17px;
  > p {
    margin: 8px 0;
    max-width: 640px;
  }
}
.game-title {
  margin: 12px 0 4px;
  font-family: "Times New Roman";
  font-size: 34px;
  font-weight: 400;
  color: var(--fluorine);
}
.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.prose {
  text-align: left;
  font-family: "PT Sans Narrow";
  font-size: 18px;
  h3 {
    margin: 0 0 8px;
    color: var(--fluorine);
    font-family: "Times New Roman";
    font-size: 26px;
    font-weight: 400;
  }
  h3:not(:first-child) {
    margin-top: 28px;
  }
  p {
    max-width: 720px;
  }
  a {
    color: var(--fluorine);
    white-space: nowrap;
  }
}

// The Constitution, styled like an official document.
.decree {
  position: relative;
  box-sizing: border-box;
  margin: 30px auto;
  padding: 40px 56px 48px;
  max-width: 820px;
  border: 1px solid rgba(228, 224, 122, 0.5);
  outline: 1px solid rgba(228, 224, 122, 0.25);
  outline-offset: 6px;
  border-radius: 4px;
  background: var(--fluorine-card);
  color: var(--fluorine-text);
  text-align: center;
}
.decree-eyebrow {
  margin: 0;
  color: var(--fluorine);
  font-size: 13px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
}
.decree-title {
  margin: 10px 0 4px;
  color: var(--fluorine);
  font-family: "Times New Roman";
  font-size: 34px;
  font-weight: 400;
}
.decree-intro {
  margin: 0 0 24px;
  font-family: "PT Sans Narrow";
  font-style: italic;
  opacity: 0.8;
}
.articles {
  margin: 0;
  padding: 0;
  list-style: none;
  text-align: left;
  li {
    display: flex;
    gap: 20px;
    align-items: baseline;
    padding: 14px 0;
    border-top: 1px solid rgba(228, 224, 122, 0.15);
  }
}
.article-number {
  flex: 0 0 64px;
  color: var(--fluorine);
  font-family: "Times New Roman";
  font-size: 44px;
  line-height: 1;
  text-align: right;
}
.article-text {
  font-family: "Times New Roman";
  font-size: 20px;
  line-height: 1.35;
}
.seal {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  margin: 28px auto 8px;
  border: 3px double var(--fluorine);
  border-radius: 50%;
  color: var(--fluorine);
  transform: rotate(-12deg);
  opacity: 0.85;
}
.seal-ring {
  position: absolute;
  inset: 10px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  border: 1px dashed rgba(228, 224, 122, 0.6);
  border-radius: 50%;
  padding-bottom: 10px;
  font-size: 8px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
}
.seal-mark {
  font-family: "Times New Roman";
  font-size: 44px;
  line-height: 1;
  margin-bottom: 10px;
}
.decree-signature {
  margin: 8px 0 0;
  font-family: "Times New Roman";
  font-style: italic;
  opacity: 0.8;
}

.table-wrap {
  overflow-x: auto;
}
.money-table {
  width: 100%;
  margin-top: 16px;
  border-collapse: collapse;
  font-size: 16px;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid rgba(228, 224, 122, 0.15);
    text-align: left;
    vertical-align: top;
  }
  th {
    color: var(--fluorine);
    font-weight: 400;
    font-size: 13px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  td:first-child {
    color: var(--fluorine);
    white-space: nowrap;
  }
}

.timeline {
  display: flex;
  margin: 8px 0 20px;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 26px;
    &::before {
      content: '';
      position: absolute;
      top: 8px;
      left: 0;
      right: 0;
      height: 2px;
      background: rgba(228, 224, 122, 0.3);
    }
    &:first-child::before { left: 9px; }
    &:last-child::before { right: auto; width: 9px; }
  }
  .dot {
    position: absolute;
    top: 0;
    left: 0;
    width: 14px;
    height: 14px;
    border: 2px solid var(--fluorine);
    border-radius: 50%;
    background: var(--fluorine-dark);
  }
  .now .dot {
    background: var(--fluorine);
  }
  strong {
    color: var(--fluorine);
    font-family: "Times New Roman";
    font-size: 24px;
    font-weight: 400;
  }
  span:not(.dot) {
    font-size: 15px;
    opacity: 0.85;
  }
}

@media (max-width: 768px) {
  .block {
    width: 100%;
  }
  .section-menu {
    top: 100px;
    a {
      padding: 6px 10px;
      font-size: 15px;
    }
  }
  .game-row {
    flex-direction: column;
  }
  .game-image {
    flex: none;
    width: 100%;
    height: 240px;
  }
  .decree {
    margin: 24px 12px;
    padding: 28px 18px 36px;
  }
  .article-number {
    flex-basis: 40px;
    font-size: 32px;
  }
  .article-text {
    font-size: 17px;
  }
  .timeline {
    flex-direction: column;
    gap: 18px;
    li {
      padding: 0 0 0 28px;
      &::before { display: none; }
    }
  }
}
</style>
